import type { ReactNode } from 'react'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import BodyText from 'LuxKit/Typography/BodyText'
import Flag from 'LuxKit/Flag'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import ContactContext from 'contexts/contactContext'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import NavBarCurrencyLanguageModal from 'components/NavKit/NavBarButtons/NavBarCurrencyLanguageModal'
import { useVisibleLanguageOptions } from 'components/NavKit/NavBarButtons/useVisibleLanguageOptions'

// Sized so "Return to luxuryescapes.com" stays on one line in the 272px side nav.
const FOOTER_LOGO_SIZE = 20

const FooterActionCard = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(6)};
  padding: ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  text-align: start;
  text-decoration: none;
`

const FooterActionContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  min-inline-size: 0;
`

const FooterActionText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-inline-size: 0;
`

const FooterActionIcon = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  inline-size: ${rem(FOOTER_LOGO_SIZE)};
  block-size: ${rem(FOOTER_LOGO_SIZE)};
`

const FooterChevron = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  inline-size: ${rem(16)};
  block-size: ${rem(16)};
`

const FooterQuickLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: ${rem(8)};
  min-block-size: ${rem(44)};
`

const FooterQuickLink = styled(Clickable)`
  display: flex;
  flex-shrink: 1;
  align-items: center;
  gap: ${rem(6)};
  min-inline-size: 0;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  text-decoration: none;
`

const FooterQuickLinkButton = styled.button`
  display: flex;
  flex-shrink: 1;
  align-items: center;
  gap: ${rem(6)};
  min-inline-size: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;
`

const FooterQuickLinkIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
`

interface Props {
  onLinkClick?: () => void
  staffTools?: ReactNode
}

function TripPlannerDrawerFooter({
  onLinkClick = noop,
  staffTools,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const supportContact = useContext(ContactContext)
  const currentCurrency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const { showLanguageSelector } = useVisibleLanguageOptions()

  const handleRegionSelectorOpen = useCallback(() => {
    showModal(
      <NavBarCurrencyLanguageModal
        showLanguageTab={showLanguageSelector}
        tabletHeight="auto"
      />,
    )
  }, [showLanguageSelector, showModal])

  const backToHomepageTitle = intl.formatMessage({
    id: 'yb2JBen_d2RnmtS5bzow',
    defaultMessage: 'Back to homepage',
    description: 'Title for the TripPlanner navigation drawer action that returns the user to the Luxury Escapes homepage',
  })
  const backToHomepageSubtitle = intl.formatMessage({
    id: 'X1rJGgiyrrb3yUqeHEi9',
    defaultMessage: 'Return to luxuryescapes.com',
    description: 'Subtitle for the TripPlanner navigation drawer action that returns the user to the Luxury Escapes homepage',
  })
  const callSupportLabel = intl.formatMessage({
    id: 'rYfHEnwTr3bsPEsspYa1',
    defaultMessage: 'Call customer support on {phoneNumber}',
    description: 'Accessibility label for the compact TripPlanner navigation drawer link that calls customer support',
  }, { phoneNumber: supportContact.defaultContact.humanReadable })
  const regionSelectorLabel = intl.formatMessage({
    id: 'rUXG5sBBRnvoF8tVg_pM',
    defaultMessage: 'Change currency or language',
    description: 'Accessibility label for the TripPlanner navigation drawer currency and language selector action',
  })

  return (
    <Group direction="vertical" gap={4}>
      <FooterActionCard to="/" onClick={onLinkClick}>
        <FooterActionContent>
          <FooterActionIcon>
            <LogoIcon height={FOOTER_LOGO_SIZE} width={FOOTER_LOGO_SIZE} mobileVariant="icon" tabletVariant="icon" desktopVariant="icon" extraLargeDesktopVariant="icon" />
          </FooterActionIcon>
          <FooterActionText>
            <BodyText variant="medium" weight="bold">
              {backToHomepageTitle}
            </BodyText>
            <BodyText variant="small" colour="light-foreground-alt">
              {backToHomepageSubtitle}
            </BodyText>
          </FooterActionText>
        </FooterActionContent>
        <FooterChevron aria-hidden="true">
          <LineAngleRightIcon size="XS" />
        </FooterChevron>
      </FooterActionCard>
      <FooterQuickLinks>
        <FooterQuickLink
          href={`tel:${supportContact.defaultContact.number}`}
          aria-label={callSupportLabel}
          onClick={onLinkClick}
        >
          <FooterQuickLinkIcon>
            <LinePhoneIcon size="S" />
          </FooterQuickLinkIcon>
          <BodyText variant="medium">
            {supportContact.defaultContact.humanReadable}
          </BodyText>
        </FooterQuickLink>
        <FooterQuickLinkButton type="button" aria-label={regionSelectorLabel} onClick={handleRegionSelectorOpen}>
          <FooterQuickLinkIcon>
            <Flag countryCode={currentRegionCode} size="S" />
          </FooterQuickLinkIcon>
          <BodyText variant="medium">
            {currentCurrency.toUpperCase()}
          </BodyText>
        </FooterQuickLinkButton>
      </FooterQuickLinks>
      {staffTools}
    </Group>
  )
}

export default TripPlannerDrawerFooter
